import React, { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { PlusCircle, Loader2, Upload, ImageIcon, X, Swords, QrCode, CalendarClock, Save, ChevronLeft, ChevronRight, CalendarDays, CheckSquare, Square, Globe, Store, Leaf, Clock, ThumbsUp, AlertTriangle, ShieldCheck, Settings2, ChevronDown, ChevronUp, Sparkles, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import api from "../api";
import { markPasskeyNudgeDue } from "../components/PasskeyNudge";
import { CURRENCIES, symbolFor } from "../utils/currency";
import { compressImage } from "../utils/image";
import { checkPhoto } from "../utils/imageCheck";
import { qaDetailText } from "../utils/qaError";
import { GMT_OFFSETS, formatGmt } from "../utils/hours";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";
import { catLabel } from "../utils/categoryLabel";
import { plausibleEvent } from "../utils/track";
import DealLocationPicker from "../components/dashboard/DealLocationPicker";
import { useEnabledCategoryNames } from "../hooks/useEnabledCategories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Short-dated stock (best-before / expiry) only makes sense for perishables — keep in sync with server.py
const SHORT_DATED_CATEGORIES = ["Food", "Drinks", "Pets", "Health", "Beauty", "Fitness", "Automotive", "Plants", "Tech", "Other"];
const DETECTED_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const TIMEZONES = Array.from(new Set([DETECTED_TZ, "UTC", "America/Port_of_Spain", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Mexico_City", "Europe/London", "Europe/Paris", "Europe/Berlin", "Africa/Lagos", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"]));

// The settings a shop sets once and rarely changes: remembered so the next post is photo, title, price.
const PREFS_KEY = "lp_deal_prefs";
const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; }
  catch { return {}; }
};

const PostDeal = () => {
  const catNames = useEnabledCategoryNames();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const undercut = location.state?.undercut || null;
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, lang } = useLang();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState([]); // [{ name, pct }] per-photo upload progress
  const [coach, setCoach] = useState([]); // [{ name, state: checking|ok|bad, text }] photo coach verdicts
  const [branches, setBranches] = useState(() => (!editId && user?.branches && user.branches.length ? user.branches.map((b) => ({ ...b, hours: "" })) : []));
  const [dealType, setDealType] = useState(undercut ? "percent" : "amount"); // 'amount' | 'percent'
  const [showMore, setShowMore] = useState(!!editId);
  const [drafting, setDrafting] = useState(false);
  const [undoDesc, setUndoDesc] = useState(null);
  const [titleIdea, setTitleIdea] = useState(null);
  const [undoTitle, setUndoTitle] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => setCfg(null)); }, []);

  const draftDescription = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/deals/draft-description", {
        title: f.title, category: f.category, lang, currency: f.currency,
        was: f.was ? parseFloat(f.was) : null,
        price: f.price ? parseFloat(f.price) : null,
        discountPct: f.discountPct ? parseInt(f.discountPct, 10) : null,
        isOnline: f.isOnline,
        image: f.images[0] || null,
      });
      setUndoDesc(f.description);
      setF((p) => ({ ...p, description: data.text }));
      setTitleIdea(data.title || null);
      if (!data.usedPhoto) toast({ title: t("pd2.draftNoPhoto") });
    } catch (e) {
      toast({ title: t("pd2.draftFailed"), description: e?.response?.data?.detail || "" });
    } finally { setDrafting(false); }
  };
  const useTitleIdea = () => {
    setUndoTitle(f.title);
    setF((p) => ({ ...p, title: titleIdea }));
    setTitleIdea(null);
  };

  const savedBranches = user?.branches || [];
  const prefs = useMemo(() => (editId ? {} : loadPrefs()), [editId]);
  const [f, setF] = useState({
    title: undercut?.title || "", category: prefs.category || "Food", description: "", images: [],
    quiet: false, quietDays: [0, 1, 2, 3, 4], quietStart: "15:00", quietEnd: "17:00",
    was: "", price: "", discountPct: undercut?.discount ? String(undercut.discount) : "",
    currency: prefs.currency || user?.currency || "USD",
    hours: "Mon-Sat 9:00 AM - 6:00 PM",
    terms: prefs.terms || "",
    nearExpiry: false, timeLeft: "1d left", featured: false, codeRequired: !!prefs.codeRequired,
    shortDated: false, bestBefore: "",
    isOnline: !!prefs.isOnline, promoCode: "",
    expiresDate: new Date(Date.now() + (prefs.days || 7) * 24 * 60 * 60 * 1000),
    expiresTime: prefs.expiresTime || "23:59",
    tzOffset: typeof prefs.tzOffset === "number" ? prefs.tzOffset : ((user?.openingHours && typeof user.openingHours.tzOffset === "number") ? user.openingHours.tzOffset : 0),
    schedule: false, publishAtLocal: "",
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // Edit mode: load the existing deal and prefill the form.
  useEffect(() => {
    if (!editId) return;
    api.get(`/deals/${editId}`).then(({ data: d }) => {
      const imgs = (d.images && d.images.length ? d.images : (d.image ? [d.image] : []));
      setDealType(d.price != null ? "amount" : "percent");
      const off = (typeof d.tzOffset === "number") ? d.tzOffset : ((user?.openingHours && typeof user.openingHours.tzOffset === "number") ? user.openingHours.tzOffset : 0);
      let expDate = null, expTime = "23:59";
      if (d.expiresAt && !isNaN(new Date(d.expiresAt))) {
        const u = new Date(d.expiresAt);
        const loc = new Date(u.getTime() + u.getTimezoneOffset() * 60000 + off * 3600000);
        const pad = (n) => String(n).padStart(2, "0");
        expDate = loc;
        expTime = `${pad(loc.getHours())}:${pad(loc.getMinutes())}`;
      }
      setF((p) => ({
        ...p,
        title: d.title || "", category: d.category || p.category, description: d.description || "",
        images: imgs, was: d.was != null ? String(d.was) : "", price: d.price != null ? String(d.price) : "",
        discountPct: d.discount != null ? String(d.discount) : "", currency: d.currency || p.currency,
        hours: d.hours || p.hours, terms: d.terms || p.terms, nearExpiry: !!d.nearExpiry,
        featured: !!d.featured, codeRequired: !!d.codeRequired, timeLeft: d.timeLeft || p.timeLeft,
        isOnline: !!d.isOnline, promoCode: d.promoCode || "",
        shortDated: !!d.shortDated, bestBefore: d.bestBefore ? String(d.bestBefore).slice(0, 10) : "",
        tzOffset: off,
        quiet: !!(d.recurringDays || []).length,
        quietDays: d.recurringDays || [1, 2, 3, 4, 5].map((n) => n - 1),
        quietStart: d.recurringStart || "15:00",
        quietEnd: d.recurringEnd || "17:00",
        expiresDate: expDate || p.expiresDate,
        expiresTime: expTime,
      }));
      if (d.branches && d.branches.length) setBranches(d.branches);
    }).catch(() => toast({ title: t("pdx.couldNotLoadDeal"), description: t("pdx.pleaseTryAgain") }));
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith("image/")).slice(0, 6);
    if (!files.length) return;
    setUploading(true);
    setCoach(files.map((file) => ({ name: file.name || "photo", state: "checking" })));
    setProgress(files.map((file) => ({ name: file.name || "photo", pct: 0 })));
    try {
      const urls = [];
      for (let idx = 0; idx < files.length; idx++) {
        const file = files[idx];
        const upd = (pct) => setProgress((prev) => prev.map((p, i) => (i === idx ? { ...p, pct } : p)));
        upd(5); // compression phase
        const compressed = await compressImage(file);
        // Photo coach: same checks the server runs, so problems are caught before the upload.
        const verdict = await checkPhoto(compressed);
        setCoach((prev) => prev.map((c, i) => (i === idx
          ? { ...c, state: verdict.ok ? "ok" : "bad", text: t(`qa.${verdict.code}`, verdict.params) }
          : c)));
        if (!verdict.ok) {
          toast({ title: t("qa.uploadBlocked"), description: t(`qa.${verdict.code}`, verdict.params) });
          continue;
        }
        upd(15);
        const form = new FormData();
        form.append("file", compressed);
        const res = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            upd(15 + Math.round((evt.loaded / evt.total) * 85));
          },
        });
        upd(100);
        urls.push(`${BACKEND_URL}${res.data.url}`);
      }
      if (urls.length) {
        setF((p) => ({ ...p, images: [...p.images, ...urls].slice(0, 6) }));
        toast({ title: t("pdx.photosAdded"), description: `${urls.length} photo(s) uploaded.` });
      }
    } catch (err) {
      toast({ title: t("qa.uploadBlocked"), description: qaDetailText(t, err.response?.data?.detail) || "Try smaller images." });
    } finally { setUploading(false); setProgress([]); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImg = (i) => setF((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }));
  // Switching deal type clears the other mode's fields so no stale value lingers.
  const switchDealType = (type) => {
    setDealType(type);
    setF((p) => (type === "amount" ? { ...p, discountPct: "" } : { ...p, was: "", price: "" }));
  };
  // Touch-friendly reorder (native drag doesn't work on mobile). dir: -1 left, +1 right.
  const moveImg = (i, dir) => setF((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.images.length) return p;
    const arr = [...p.images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...p, images: arr };
  });
  const dragIdx = useRef(null);
  const onDrop = (i) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from == null || from === i) return;
    setF((p) => {
      const arr = [...p.images];
      const [moved] = arr.splice(from, 1);
      arr.splice(i, 0, moved);
      return { ...p, images: arr };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.isOnline && !f.promoCode.trim()) {
      toast({ title: t("pd.promoNeeded"), description: t("pd.promoNeededMsg") });
      return;
    }
    const percent = dealType === "percent";
    if (f.description.trim().length < 20) {
      toast({ title: t("qa.fixTitle"), description: t("qa.desc_short") });
      return;
    }
    if (percent && !(parseInt(f.discountPct, 10) > 0)) {
      toast({ title: t("pd.addDiscount"), description: t("pd.addDiscountMsg") });
      return;
    }
    const was = parseFloat(f.was) || 0;
    const price = parseFloat(f.price) || 0;
    const discount = percent
      ? Math.max(0, Math.min(100, parseInt(f.discountPct, 10) || 0))
      : (was > 0 && price > 0 ? Math.round((1 - price / was) * 100) : 0);
    const save = percent ? null : (was > 0 && price > 0 ? +(was - price).toFixed(2) : 0);
    const images = f.images.length ? f.images : ["https://images.unsplash.com/photo-1556909212-d5b604d0c90d?w=800&q=80"];
    // Turn the picked end-date + time into a local timestamp interpreted at the chosen GMT offset.
    const pad = (n) => String(n).padStart(2, "0");
    const ed = f.expiresDate ? new Date(f.expiresDate) : null;
    const expiresAtLocal = ed ? `${ed.getFullYear()}-${pad(ed.getMonth() + 1)}-${pad(ed.getDate())}T${f.expiresTime || "23:59"}` : null;
    setSaving(true);
    try {
      const payload = {
        title: f.title, category: f.category, description: f.description,
        image: images[0], images,
        discount,
        price: percent ? null : price,
        was: percent ? null : was,
        save,
        country: user?.country || "Trinidad & Tobago",
        location: f.isOnline ? "Online" : (branches[0]?.address || branches[0]?.label || user?.location || ""),
        currency: f.currency, branches: f.isOnline ? [] : branches.filter((b) => b.lat != null),
        terms: f.terms.trim() || t("pd.termsDefault"), nearExpiry: f.nearExpiry, featured: f.featured, timeLeft: f.timeLeft,
        codeRequired: f.isOnline ? false : f.codeRequired,
        isOnline: f.isOnline,
        shortDated: f.shortDated,
        bestBefore: f.bestBefore || null,
        promoCode: f.isOnline ? f.promoCode.trim() : "",
        expiresAtLocal,
        tzOffset: f.tzOffset,
        publishAtLocal: (f.schedule && f.publishAtLocal) ? f.publishAtLocal : null,
        recurringDays: f.quiet ? f.quietDays : [],
        recurringStart: f.quiet ? f.quietStart : "",
        recurringEnd: f.quiet ? f.quietEnd : "",
      };
      const res = editId ? await api.put(`/deals/${editId}`, payload) : await api.post("/deals", payload);
      if (!editId) plausibleEvent("Deal Posted", { category: f.category || "" });
      if (res.data?.pendingApproval) {
        const hold = res.data.holdReason;
        const title = hold === "email_unverified" ? t("qa.emailPending") : hold === "first_deal" ? t("qa.firstDealPending") : t("qa.pending");
        const desc = hold === "email_unverified" ? t("qa.emailPendingMsg") : hold === "first_deal" ? t("qa.firstDealPendingMsg") : t("qa.pendingMsg");
        toast({ title, description: desc });
        navigate("/account");
        return;
      }
      const scheduledFuture = f.schedule && f.publishAtLocal && new Date(f.publishAtLocal) > new Date();
      if (res.data?.expiryClamped) {
        toast({ title: t("pd.clampedTitle"), description: t("pd.clampedNote", { n: res.data.maxDealDays }) });
      }
      toast({
        title: editId ? "Deal updated!" : (scheduledFuture ? "Deal scheduled!" : "Deal posted!"),
        description: editId ? `"${f.title}" has been updated.` : (scheduledFuture ? `${f.title} goes live ${f.publishAtLocal.replace("T", " ")} (${formatGmt(f.tzOffset)}).` : `${f.title} is now live.`),
      });
      if (!editId) markPasskeyNudgeDue();
      try {
        const days = Math.max(0, Math.round((new Date(f.expiresDate) - Date.now()) / 86400000));
        localStorage.setItem(PREFS_KEY, JSON.stringify({
          category: f.category, currency: f.currency, terms: f.terms, isOnline: f.isOnline,
          codeRequired: f.codeRequired, tzOffset: f.tzOffset, expiresTime: f.expiresTime, days,
        }));
      } catch { /* a full storage quota must never block a post */ }
      navigate(`/deal/${res.data.id || editId}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402 && d?.code === "plan_limit") {
        toast({
          title: t("qa.planLimit"),
          description: d.message,
          action: <button onClick={() => navigate("/pricing")} data-testid="plan-limit-upgrade" className="text-xs font-bold bg-[#16a34a] text-white rounded-lg px-3 py-2">{t("qa.seePlans")}</button>,
        });
      } else if (d?.code === "unverified_live_limit") {
        toast({
          title: t("qa.liveCapTitle"), description: d.message,
          action: <button onClick={() => navigate("/account")} data-testid="live-cap-verify-cta" className="text-xs font-bold bg-[#16a34a] text-white rounded-lg px-3 py-2">{t("vf.upload")}</button>,
        });
      } else if (d?.code === "needs_verification") {
        toast({
          title: t("vf.title"), description: qaDetailText(t, d),
          action: <button onClick={() => navigate("/account")} data-testid="verify-cta" className="text-xs font-bold bg-[#16a34a] text-white rounded-lg px-3 py-2">{t("vf.upload")}</button>,
        });
      } else if (d?.code === "deal_qa" || d?.code) {
        setShowMore(true); // whatever they have to fix may be hidden away
        toast({ title: t("qa.fixTitle"), description: qaDetailText(t, d) });
      } else {
        toast({
          title: editId ? "Could not update" : "Could not post",
          description: (typeof d === "string" ? d : d?.message) || "Something went wrong. Please try again.",
        });
      }
    } finally { setSaving(false); }
  };

  const input = "w-full border border-border rounded-xl px-4 py-3 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground";

  // Deals have a shelf life per plan, so the date picker can't offer something the server will pull back.
  const [maxDays, setMaxDays] = useState(null);
  useEffect(() => {
    api.get("/my/credits").then(({ data }) => setMaxDays(data.maxDealDays || null)).catch(() => {});
  }, []);
  const maxEnd = useMemo(() => {
    if (!maxDays) return undefined;
    const d = new Date(Date.now() + maxDays * 86400000);
    d.setHours(23, 59, 0, 0);
    return d;
  }, [maxDays]);
  useEffect(() => {
    if (maxEnd && f.expiresDate && new Date(f.expiresDate) > maxEnd) setF((p) => ({ ...p, expiresDate: maxEnd }));
  }, [maxEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const DAY_CHIPS = [[0, "pd2.endToday"], [3, "pd2.end3"], [7, "pd2.end7"], [14, "pd2.end14"]];
  const endInDays = (n) => {
    const d = new Date(Date.now() + n * 86400000);
    d.setHours(23, 59, 0, 0);
    setF((p) => ({ ...p, expiresDate: maxEnd && d > maxEnd ? maxEnd : d, expiresTime: "23:59" }));
  };
  const daysUntil = (() => {
    if (!f.expiresDate) return null;
    const a = new Date(f.expiresDate); a.setHours(0, 0, 0, 0);
    const b = new Date(); b.setHours(0, 0, 0, 0);
    return Math.round((a - b) / 86400000);
  })();

  const endsOnField = (
    <div data-testid="ends-on-field">
      <label className="text-sm font-medium text-foreground block mb-2">{t("pd.endsOn")}</label>
      <div className="flex flex-wrap items-center gap-2">
        {DAY_CHIPS.map(([n, key]) => (
          <button type="button" key={n} onClick={() => endInDays(n)} data-testid={`end-chip-${n}`}
            disabled={maxDays != null && n > maxDays}
            className={`text-sm font-bold rounded-xl px-4 py-2.5 border transition-colors disabled:opacity-40 ${daysUntil === n
              ? "bg-[#16a34a] text-white border-[#16a34a]"
              : "bg-card text-foreground border-border hover:border-[#16a34a]"}`}>
            {t(key)}
          </button>
        ))}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="expiry-date-trigger"
              className="text-sm font-semibold rounded-xl px-4 py-2.5 border border-border hover:border-[#16a34a] text-muted-foreground inline-flex items-center gap-2 transition-colors">
              <CalendarDays size={15} className="text-[#16a34a]" /> {t("pd2.endPick")}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={f.expiresDate} onSelect={(d) => d && setF((p) => ({ ...p, expiresDate: d }))} disabled={{ before: new Date(), after: maxEnd }} initialFocus data-testid="expiry-calendar" />
          </PopoverContent>
        </Popover>
      </div>
      <p className="text-xs text-muted-foreground mt-2" data-testid="ends-on-summary">
        {f.expiresDate
          ? t("pd2.endsAt", { date: format(f.expiresDate, "EEE, d MMM yyyy"), time: f.expiresTime || "23:59", gmt: formatGmt(f.tzOffset) })
          : t("pd2.endPick")}
      </p>
      {maxDays && (
        <p className="text-xs text-muted-foreground mt-1" data-testid="max-days-note">{t("pd.maxDays", { n: maxDays })}</p>
      )}
    </div>
  );

  const exactTimeField = (
    <div className="sm:col-span-2" data-testid="exact-time-field">
      <label className="text-sm font-medium text-foreground block mb-1">{t("pd2.exactTime")}</label>
      <div className="flex flex-wrap items-center gap-2">
        <input type="time" value={f.expiresTime} onChange={set("expiresTime")} data-testid="expiry-time" className="border border-border rounded-xl px-3 py-3 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]" />
        <select value={f.tzOffset} onChange={(e) => setF((p) => ({ ...p, tzOffset: parseFloat(e.target.value) }))} data-testid="expiry-tz" className="border border-border rounded-xl px-3 py-3 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]">
          {GMT_OFFSETS.map((o) => <option key={o} value={o}>{formatGmt(o)}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 lp-fade-up max-w-2xl">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2">{editId ? <Save className="text-[#16a34a]" /> : <PlusCircle className="text-[#16a34a]" />} {editId ? t("pd.editTitle") : t("pd.postTitle")}</h1>
        <p className="text-sm text-muted-foreground">{editId ? t("pd.editSubtitle") : t("pd.subtitle")}</p>
      </div>

      {user?.trust === "watch" && (
        <div className="bg-amber-50 dark:bg-[#3a2f10] border border-amber-300/50 rounded-2xl p-4 flex items-start gap-3" data-testid="watch-notice">
          <span className="h-9 w-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><ShieldCheck size={18} /></span>
          <p className="text-sm text-amber-900 dark:text-amber-100">{t("qa.watchNotice")}</p>
        </div>
      )}

      {undercut && (
        <div className="bg-[#fde8ee] dark:bg-[#3b0d1c] border border-[#e11d48]/30 rounded-2xl p-4 flex items-start gap-3" data-testid="undercut-banner">
          <span className="h-9 w-9 rounded-xl bg-[#e11d48] text-white flex items-center justify-center shrink-0"><Swords size={18} /></span>
          <div className="text-sm">
            <p className="font-bold text-foreground">{t("pd.beating", { rival: undercut.rivalBusiness })}</p>
            <p className="text-muted-foreground">{t("pd.beatingMsg", { title: undercut.rivalTitle, rivalDiscount: undercut.rivalDiscount, discount: undercut.discount })}</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground block mb-1">{t("pd.dealTitle")}</label>
          <input required value={f.title} onChange={set("title")} placeholder={t("pd.dealTitlePh")} data-testid="deal-title" className={input} />
          {titleIdea && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] px-3 py-2" data-testid="ai-title-suggestion">
              <Sparkles size={13} className="text-[#16a34a] shrink-0" />
              <span className="text-xs text-muted-foreground">{t("pd2.titleSuggested")}</span>
              <span className="text-xs font-bold text-foreground">{titleIdea}</span>
              <button type="button" onClick={useTitleIdea} data-testid="ai-title-use"
                className="text-xs font-bold text-[#16a34a] hover:underline">{t("pd2.useTitle")}</button>
              <button type="button" onClick={() => setTitleIdea(null)} data-testid="ai-title-dismiss"
                className="text-xs font-semibold text-muted-foreground hover:text-foreground">{t("pd2.dismissTitle")}</button>
            </div>
          )}
          {undoTitle !== null && (
            <button type="button" onClick={() => { setF((p) => ({ ...p, title: undoTitle })); setUndoTitle(null); }}
              data-testid="ai-title-undo"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw size={13} /> {t("pd2.undoTitleChange")}
            </button>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground block mb-2">{t("pd.photos")} <span className="text-muted-foreground font-normal">{t("pd.photosHint")}</span></label>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
          {coach.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="photo-coach">
              {coach.map((c, i) => (
                <div key={i} data-testid={`photo-coach-${i}`}
                  className={`flex items-center gap-2 text-xs rounded-xl px-3 py-2 ${c.state === "ok" ? "bg-[#e7f6ec] text-[#15803d]" : c.state === "bad" ? "bg-rose-50 text-rose-700" : "bg-secondary text-muted-foreground"}`}>
                  {c.state === "ok" ? <ThumbsUp size={13} /> : c.state === "bad" ? <AlertTriangle size={13} /> : <Loader2 size={13} className="animate-spin" />}
                  <span className="font-semibold truncate max-w-[40%]">{c.name}</span>
                  <span className="truncate">{c.state === "checking" ? t("qa.coachChecking") : c.text}</span>
                </div>
              ))}
            </div>
          )}
          {progress.length > 0 && (
            <div className="mb-3 space-y-2" data-testid="upload-progress">
              {progress.map((p, i) => (
                <div key={i} data-testid={`upload-progress-${i}`}>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span className="truncate max-w-[70%]">{p.name}</span>
                    <span className="font-semibold text-[#16a34a]">{p.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-[#16a34a] rounded-full transition-all duration-200" style={{ width: `${p.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {f.images.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {f.images.map((src, i) => (
                <div key={i} draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} className="relative h-28 rounded-xl overflow-hidden border border-border cursor-move group">
                  <img src={src} alt={`photo ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                  {i === 0 && <span className="absolute top-1 left-1 bg-[#16a34a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{t("pd.cover")}</span>}
                  <button type="button" onClick={() => removeImg(i)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={13} /></button>
                  <div className="absolute bottom-1 inset-x-1 flex items-center justify-between">
                    <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} data-testid={`move-photo-left-${i}`} aria-label={t("pdx.movePhotoLeft")} className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"><ChevronLeft size={14} /></button>
                    <button type="button" onClick={() => moveImg(i, 1)} disabled={i === f.images.length - 1} data-testid={`move-photo-right-${i}`} aria-label={t("pdx.movePhotoRight")} className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"><ChevronRight size={14} /></button>
                  </div>
                </div>
              ))}
              {f.images.length < 6 && (
                <button type="button" onClick={() => fileRef.current?.click()} className="h-28 rounded-xl border-2 border-dashed border-border hover:border-[#16a34a] flex flex-col items-center justify-center gap-1 text-muted-foreground text-xs transition-colors">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} className="text-[#16a34a]" />} Add more
                </button>
              )}
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full h-40 rounded-xl border-2 border-dashed border-border hover:border-[#16a34a] flex flex-col items-center justify-center gap-2 text-muted-foreground transition-colors">
              {uploading ? <Loader2 className="animate-spin" size={24} /> : <Upload size={24} className="text-[#16a34a]" />}
              <span className="text-sm font-medium">{uploading ? t("pd.uploading") : t("pd.upload")}</span>
              <span className="text-xs flex items-center gap-1"><ImageIcon size={12} /> {t("pd.uploadHint")}</span>
            </button>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground block mb-1">{t("pd.description")}</label>
          <textarea required value={f.description} onChange={set("description")} rows={3} placeholder={t("pd.descriptionPh")} data-testid="deal-description" className={input + " resize-none"} />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {cfg?.aiDescription !== false && (
              <button type="button" onClick={draftDescription} disabled={drafting || !f.title.trim()} data-testid="ai-draft-btn"
                className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] disabled:opacity-40 px-3 py-2 rounded-xl transition-colors">
                {drafting ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} className="text-[#16a34a]" />}
                {t("pd2.writeForMe")}
              </button>
            )}
            {cfg?.aiDescription !== false && !f.images.length && (
              <span className="text-xs text-muted-foreground" data-testid="ai-photo-nudge">{t("pd2.photoFirst")}</span>
            )}
            {undoDesc !== null && (
              <button type="button" onClick={() => { setF((p) => ({ ...p, description: undoDesc })); setUndoDesc(null); }}
                data-testid="ai-draft-undo"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors">
                <RotateCcw size={13} /> {t("pd2.undoDraft")}
              </button>
            )}
          </div>
          <p className={`text-xs mt-1 ${f.description.trim().length >= 20 ? "text-muted-foreground" : "text-[#b8860b]"}`} data-testid="description-hint">
            {f.description.trim().length >= 20
              ? t("pd2.descOk")
              : t("pd2.descHint", { n: 20 - f.description.trim().length })}
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground block mb-2">{t("pd.dealType")}</label>
          <div className="inline-flex bg-secondary rounded-xl p-1 mb-3" data-testid="deal-type-toggle">
            <button type="button" onClick={() => switchDealType("amount")} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${dealType === "amount" ? "bg-[#16a34a] text-white shadow" : "text-muted-foreground"}`} data-testid="deal-type-amount">{t("pd.setPrice")} ({symbolFor(f.currency)})</button>
            <button type="button" onClick={() => switchDealType("percent")} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${dealType === "percent" ? "bg-[#16a34a] text-white shadow" : "text-muted-foreground"}`} data-testid="deal-type-percent">{t("pd.percentOff")}</button>
          </div>
          {dealType === "amount" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-foreground block mb-1">{t("pd.originalPrice", { sym: symbolFor(f.currency) })}</label><input type="number" step="0.01" value={f.was} onChange={set("was")} placeholder="120" className={input} /></div>
              <div><label className="text-sm font-medium text-foreground block mb-1">{t("pd.dealPrice", { sym: symbolFor(f.currency) })}</label><input type="number" step="0.01" value={f.price} onChange={set("price")} placeholder="60" className={input} /></div>
            </div>
          ) : (
            <div className="sm:max-w-[16rem]">
              <label className="text-sm font-medium text-foreground block mb-1">{t("pd.discountPct")}</label>
              <input type="number" min="1" max="100" value={f.discountPct} onChange={set("discountPct")} placeholder="e.g. 25" className={input} data-testid="deal-percent-input" />
            </div>
          )}
        </div>

        {endsOnField}

        <div>
          <label className="text-sm font-medium text-foreground block mb-2">{t("pd.redeemHow")}</label>
          <div className="inline-flex bg-secondary rounded-xl p-1 mb-3" data-testid="redeem-mode-toggle">
            <button type="button" onClick={() => setF((p) => ({ ...p, isOnline: false }))} data-testid="redeem-mode-instore" className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${!f.isOnline ? "bg-[#16a34a] text-white shadow" : "text-muted-foreground"}`}><Store size={15} /> {t("pd.inPerson")}</button>
            <button type="button" onClick={() => setF((p) => ({ ...p, isOnline: true }))} data-testid="redeem-mode-online" className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${f.isOnline ? "bg-[#16a34a] text-white shadow" : "text-muted-foreground"}`}><Globe size={15} /> {t("pd.onlineOnly")}</button>
          </div>
          {f.isOnline ? (
            <div className="rounded-xl border border-[#16a34a]/40 bg-[#e7f6ec] dark:bg-[#16341f] p-4" data-testid="online-deal-fields">
              <p className="text-sm text-foreground font-semibold flex items-center gap-1.5"><Globe size={15} className="text-[#16a34a]" /> {t("pd.onlineNote")}</p>
              <label className="text-sm font-medium text-foreground block mt-3 mb-1">{t("pd.promoLabel")}</label>
              <input value={f.promoCode} onChange={(e) => setF((p) => ({ ...p, promoCode: e.target.value.toUpperCase() }))} placeholder={t("pdx.eGLyte")} data-testid="promo-code-input" className={input + " uppercase tracking-widest font-bold"} />
              <p className="text-xs text-muted-foreground mt-1">{t("pd.promoHint")}</p>
            </div>
          ) : savedBranches.length > 0 ? (
            <div className="rounded-xl border border-border p-3 bg-secondary/40" data-testid="saved-branch-picker">
              <p className="text-sm font-medium text-foreground mb-2">{t("pd.whichLocations")}</p>
              <div className="flex flex-wrap gap-2">
                {savedBranches.map((sb, si) => {
                  const isSel = branches.some((b) => Math.abs((b.lat ?? 0) - sb.lat) < 1e-6 && Math.abs((b.lng ?? 0) - sb.lng) < 1e-6);
                  const toggle = () => {
                    if (isSel) setBranches((prev) => prev.filter((b) => !(Math.abs((b.lat ?? 0) - sb.lat) < 1e-6 && Math.abs((b.lng ?? 0) - sb.lng) < 1e-6)));
                    else setBranches((prev) => [...prev, { ...sb, hours: "" }]);
                  };
                  return (
                    <button type="button" key={si} onClick={toggle} data-testid={`saved-branch-${si}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${isSel ? "bg-[#16a34a] text-white border-[#16a34a]" : "bg-card text-foreground border-border hover:border-[#16a34a]/50"}`}>
                      {isSel ? <CheckSquare size={14} /> : <Square size={14} />} {sb.label || sb.address || `Branch ${si + 1}`}{sb.currency ? ` · ${sb.currency}` : ""}
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={() => setShowMore(true)} data-testid="pin-other-spot"
                className="text-xs font-bold text-[#16a34a] hover:underline mt-2">{t("pd2.addSpot")}</button>
            </div>
          ) : (
            <>
              <label className="text-sm font-medium text-foreground block mb-1">{t("pd.pinLabel")} <span className="text-muted-foreground font-normal">{t("pd.pinHint")}</span></label>
              <DealLocationPicker testId="deal-location-picker" branches={branches} onChange={setBranches} defaultHours="" showCurrency showPrice={dealType === "amount"} baseCurrency={f.currency} defaultCenter={user?.lat != null ? { lat: user.lat, lng: user.lng } : undefined} />
            </>
          )}
        </div>

        <button type="button" onClick={() => setShowMore((s) => !s)} data-testid="more-options-toggle"
          className="w-full flex items-center justify-between gap-3 rounded-xl border border-border hover:border-[#16a34a] px-4 py-3 text-left transition-colors">
          <span>
            <span className="text-sm font-bold text-foreground flex items-center gap-1.5"><Settings2 size={15} className="text-[#16a34a]" /> {t("pd2.moreOptions")}</span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              {catLabel(f.category, t)} · {f.currency}{f.codeRequired ? ` · ${t("pd2.codesOn")}` : ""}{f.featured ? ` · ${t("pd2.featuredOn")}` : ""}
            </span>
          </span>
          {showMore ? <ChevronUp size={18} className="text-muted-foreground shrink-0" /> : <ChevronDown size={18} className="text-muted-foreground shrink-0" />}
        </button>

        {showMore && (
        <div className="space-y-4 rounded-xl border border-border p-4" data-testid="more-options">
          <p className="text-xs text-muted-foreground">{t("pd2.moreHint")}</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-foreground block mb-1">{t("pd.category")}</label><select value={f.category} onChange={set("category")} className={input}>{catNames.map((c) => <option key={c} value={c}>{catLabel(c, t)}</option>)}</select></div>
            <div><label className="text-sm font-medium text-foreground block mb-1">{t("pd.currency")}</label><select value={f.currency} onChange={set("currency")} data-testid="postdeal-currency-select" className={input}>{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</select></div>
            {exactTimeField}
          </div>

          <div><label className="text-sm font-medium text-foreground block mb-1">{t("pd.terms")}</label><textarea value={f.terms} onChange={set("terms")} rows={2} placeholder={t("pd.termsDefault")} data-testid="terms-input" className={input + " resize-none"} /></div>

          {!f.isOnline && savedBranches.length > 0 && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">{t("pd.pinLabel")} <span className="text-muted-foreground font-normal">{t("pd.pinHint")}</span></label>
              <DealLocationPicker testId="deal-location-picker" branches={branches} onChange={setBranches} defaultHours="" showCurrency showPrice={dealType === "amount"} baseCurrency={f.currency} defaultCenter={user?.lat != null ? { lat: user.lat, lng: user.lng } : undefined} />
              <p className="text-xs text-muted-foreground mt-1">{t("pd.branchFooter", { place: `${user?.location || ""}${user?.country ? `, ${user.country}` : ""}` })} <button type="button" onClick={() => navigate("/account")} className="text-[#16a34a] font-semibold hover:underline">{t("nav.myAccount")}</button>.</p>
            </div>
          )}

          <div className="rounded-xl border border-border p-4 bg-secondary/40 space-y-3">
            <p className="text-xs text-muted-foreground flex items-start gap-2"><Clock size={14} className="text-[#16a34a] shrink-0 mt-0.5" /> <span><span className="font-semibold text-foreground">{t("pd.lastChance")}</span> {t("pd.lastChanceNote")}</span></p>
            {SHORT_DATED_CATEGORIES.includes(f.category) && (
              <>
                <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer" data-testid="short-dated-toggle">
                  <input type="checkbox" checked={f.shortDated} onChange={set("shortDated")} className="accent-[#16a34a] h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-semibold flex items-center gap-1.5"><Leaf size={15} className="text-[#16a34a]" /> {t("pd.shortDated")}</span>
                    <span className="text-muted-foreground text-xs block mt-0.5">{t("pd.shortDatedHint")}</span>
                  </span>
                </label>
                {f.shortDated && (
                  <div className="pl-7" data-testid="best-before-field">
                    <label className="text-xs font-medium text-foreground block mb-1">{t("pd.bestBefore")}</label>
                    <input type="date" value={f.bestBefore} onChange={set("bestBefore")} data-testid="best-before-input" className="border border-border rounded-lg px-3 py-2 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]" />
                  </div>
                )}
              </>
            )}
            <label className="flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={f.featured} onChange={set("featured")} className="accent-[#16a34a] h-4 w-4" /> {t("pd.feature")}</label>
          </div>

          <div className="rounded-xl border border-border p-4 bg-secondary/40" hidden={f.isOnline}>
            <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer" data-testid="code-required-toggle">
              <input type="checkbox" checked={f.codeRequired} onChange={set("codeRequired")} className="accent-[#16a34a] h-4 w-4 mt-0.5" />
              <span>
                <span className="font-semibold flex items-center gap-1.5"><QrCode size={15} className="text-[#16a34a]" /> {t("pd.codeReq")}</span>
                <span className="text-muted-foreground text-xs block mt-0.5">{t("pd.codeReqHint")}</span>
              </span>
            </label>
          </div>

          <div className="rounded-xl border border-border p-4 bg-secondary/40">
            <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer" data-testid="schedule-toggle">
              <input type="checkbox" checked={f.schedule} onChange={set("schedule")} className="accent-[#16a34a] h-4 w-4 mt-0.5" />
              <span>
                <span className="font-semibold flex items-center gap-1.5"><CalendarClock size={15} className="text-[#16a34a]" /> {t("pd.schedule")}</span>
                <span className="text-muted-foreground text-xs block mt-0.5">{t("pd.scheduleHint")}</span>
              </span>
            </label>
            {f.schedule && (
              <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="schedule-fields">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">{t("pd.timezone")}</label>
                  <select value={f.tzOffset} onChange={(e) => setF((p) => ({ ...p, tzOffset: parseFloat(e.target.value) }))} data-testid="schedule-timezone" className={input}>
                    {GMT_OFFSETS.map((o) => <option key={o} value={o}>{formatGmt(o)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">{t("pd.publishAt")}</label>
                  <input type="datetime-local" value={f.publishAtLocal} onChange={set("publishAtLocal")} data-testid="schedule-publish" className={input} />
                  <p className="text-xs text-muted-foreground mt-1">{t("pd.publishHint")}</p>
                </div>
              </div>
            )}
            <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer mt-4 pt-4 border-t border-border" data-testid="quiet-hours-toggle">
              <input type="checkbox" checked={!!f.quiet} onChange={set("quiet")} className="accent-[#16a34a] h-4 w-4 mt-0.5" />
              <span>
                <span className="font-semibold flex items-center gap-1.5"><CalendarClock size={15} className="text-[#16a34a]" /> {t("pd.quietHours")}</span>
                <span className="text-muted-foreground text-xs block mt-0.5">{t("pd.quietHoursHint")}</span>
              </span>
            </label>
            {f.quiet && (
              <div className="mt-4 space-y-4" data-testid="quiet-hours-fields">
                <div className="flex flex-wrap gap-2">
                  {[[0, "pd.mon"], [1, "pd.tue"], [2, "pd.wed"], [3, "pd.thu"], [4, "pd.fri"], [5, "pd.sat"], [6, "pd.sun"]].map(([n, key]) => (
                    <button key={n} type="button" data-testid={`quiet-day-${n}`}
                      onClick={() => setF((p) => ({ ...p, quietDays: (p.quietDays || []).includes(n) ? p.quietDays.filter((x) => x !== n) : [...(p.quietDays || []), n] }))}
                      className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${(f.quietDays || []).includes(n) ? "bg-[#16a34a] border-[#16a34a] text-white" : "border-border text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
                      {t(key)}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">{t("pd.quietFrom")}</label>
                    <input type="time" value={f.quietStart || "15:00"} onChange={set("quietStart")} data-testid="quiet-start" className={input} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">{t("pd.quietTo")}</label>
                    <input type="time" value={f.quietEnd || "17:00"} onChange={set("quietEnd")} data-testid="quiet-end" className={input} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">{t("pd.timezone")}</label>
                    <select value={f.tzOffset} onChange={(e) => setF((p) => ({ ...p, tzOffset: parseFloat(e.target.value) }))} data-testid="quiet-timezone" className={input}>
                      {GMT_OFFSETS.map((o) => <option key={o} value={o}>{formatGmt(o)}</option>)}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t("pd.quietNote")}</p>
              </div>
            )}
          </div>
        </div>
        )}
        <button disabled={saving || uploading} className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">{saving ? <Loader2 className="animate-spin" size={18} /> : (editId ? <Save size={18} /> : <PlusCircle size={18} />)} {editId ? t("pd.saveChanges") : t("pd.publish")}</button>
      </form>
    </div>
  );
};

export default PostDeal;
